/*
defines information about different APIs
and functions for getting the correct values from the responses
*/

import {
    colorPrintMetaData,
    getMatches,
    stripHTML,
    makeLinksIntoButtons,
    addIconToNode,
} from './utilities.js';

/**
 * Represents information about APIs
 */
let apis = {
    // callable APIs
    available: ['cooper', 'wiki', 'harvard', 'wiktionary', 'news', 'giphy', 'natgeo', 'scientist'],
};

// example use
// apiSource = api.news.random()
// returns { type, url, request, ... }

apis.news = {
    name: 'News',
    common: {
        baseUrl: 'https://newsapi.org/v2',
        key: '1adf231505e349c2810500bee60b6f68',
        style: 'news',
        index: 0,
        getNext: function() {
            return this.index++;
        },
    },
    random: function() {
        let random = {
            // request data
            type: 'wiki',
            url: this.common.baseUrl + '/top-headlines',
            request: {
                apiKey: this.common.key,
                country: 'us',
            },
            headers: {},
            name: 'random',
            // response parsing
            text: function(response) {
                let string = response.articles[0].description || response.articles[0].title +
                             '\n' + response.articles[0].url;
                let newText = addIconToNode('news', string);
                return newText;
            },
            isEmpty: function(response) {
                return false;
            },
            title: function(response) {
                return response.articles[this.common.index].title;
            },
        };
        _.assign(random, this.common);
        return random;
    },
    search: function(query) {
        let search = {
            type: 'wiki',
            url: this.common.baseUrl + '/everything',
            request: {
                apiKey: this.common.key,
                q: query,
            },
            headers: {},
            name: 'search',
            // response parsing
            text: function(response) {
                if (response.articles.length > 0) {
                    let string = response.articles[0].description || response.articles[0].title
                    + '\n' + response.articles[0].url;
                    let newText = addIconToNode('news', string);
                    return newText;
                } else return;
            },
            image: function(response) {
                return {
                    url: response.articles[0].urlToImage,
                };
            },
            isEmpty: function(response) {
                return response.articles.length === 0;
            },
            title: function(response) {
                return response.articles[0].title;
            },
        };
        _.assign(search, this.common);
        return search;
    },
};
/*

~~   How to add a content source ~~ see api.md

*/
/**
 * ADD APIS BELOW
 */

// info about Cooper Hewitt Museum API
apis.cooper = {
    name: 'Cooper Hewitt Design Museum',

    // common information for cooper API
    common: {
        type: 'image',
        url: 'https://api.collection.cooperhewitt.org/rest/',
        token: '4c7491da43107a76b11fe0ba1e51b6cd',
        style: 'cooper',
        // the following elements from the metadata to include in tooltip
        filter: [
            'title',
            'year_start',
            'medium',
            'type',
            'url',
            'description',
            'participants.0.person_name',
            'participants.1.person_name',
            'connections',
        ],
        style: 'cooper',
    },
    // random object
    random: function() {
        let random = {
            request: {
                method: 'cooperhewitt.objects.getRandom',
                has_image: 1,
                access_token: this.common.token,
            },
            name: 'random',
            headers: {},
            // gets an image object given a cooper response object
            image: function(response) {
                return response.object.images[0].n;
            },
            // gets a title/tooltip/name given a cooper response object
            title: function(response) {
                return response.object.title;
            },
            metadata: function(response) {
                return colorPrintMetaData(_.pick(_.assign({}, response.object, {
                    'connections': '_',
                }), this.filter), this.name);
            },
            // checks if the given response is empty
            isEmpty: function(response) {
                return false;
            },
        };
        _.assign(random, this.common);
        return random;
    },

    // search object
    search: function(q) {
        let search = {
            // gets a request object for requesting an object search
            request: {
                method: 'cooperhewitt.search.objects',
                query: q,
                has_images: 1,
                per_page: 1,
                page: 1,
                access_token: this.common.token,
            },
            name: 'search',
            index: 0,
            headers: {},
            // gets an image of the first object given a cooper response object
            image: function(response) {
                return response.objects[0].images[0].n;
            },
            // gets the title for the first object given a cooper response object
            title: function(response) {
                return response.objects[0].title;
            },
            metadata: function(response) {
                const filteredMetaData = _.pick(_.assign(
                        {},
                        response.objects[0],
                        {'connections': '_'}),
                    this.filter);
                return colorPrintMetaData(filteredMetaData, 'Cooper Hewitt', q);
            },
            // checks if the given response is empty
            isEmpty: function(response) {
                return response.total === 0;
            },
        };
        _.assign(search, this.common);
        return search;
    },
};

apis.giphy = {
    name: 'Giphy',

    // common information for giphy API
    common: {
        type: 'image',
        api_key: 'Gv7Y1DujihrsY4Rf4Db1YMGvbJP0qTgg',
        style: 'giphy',
        // the following elements from the metadata to include in tooltip
        filter: [
            'username',
            'title',
            'create_datetime',
        ],
    },
    // random object
    random: function() {
        let random = {
            url: 'http://api.giphy.com/v1/gifs/random',
            request: {
                api_key: this.common.api_key,
            },
            name: 'random',
            headers: {},
            // gets an image object given a giphy response object
            image: function(response) {
                return response.data.images.original;
            },
            // gets a title/tooltip/name given a giphy response object
            title: function(response) {
                return response.data.title;
            },
            metadata: function(response) {
                return colorPrintMetaData(_.pick(_.assign({}, response.data, {
                    'connections': '_',
                }), this.filter), this.name);
            },
            // checks if the given response is empty
            isEmpty: function(response) {
                return false;
            },
        };
        _.assign(random, this.common);
        return random;
    },

    // search object
    search: function(q) {
        let search = {
            url: 'http://api.giphy.com/v1/gifs/search',
            // gets a request object for requesting an object search
            request: {
                q: q,
                api_key: this.common.api_key,
            },
            name: 'search',
            index: 0,
            headers: {},
            // gets an image of the first object given a giphy response object
            image: function(response) {
                return response.data[0].images.original;
            },
            // gets the title for the first object given a giphy response object
            title: function(response) {
                return response.data[0].title;
            },
            metadata: function(response) {
                const filteredMetaData = _.pick(_.assign(
                        {},
                        response.data[0],
                        {'connections': '_'}),
                    this.filter);
                return colorPrintMetaData(filteredMetaData, 'Giphy', q);
            },
            // checks if the given response is empty
            isEmpty: function(response) {
                return response.data.length === 0;
            },
        };
        _.assign(search, this.common);
        return search;
    },
};

apis.wiki = {
    name: 'Wikipedia',
    common: {
        type: 'wiki',
        // url: "https://en.wikipedia.org/w/api.php"
        filter: [
            'title',
            'extract',

            'connections',
        ],
        style: 'wiki',

    },
    search: function(q) {
        let search = {
            url: 'https://en.wikipedia.org/api/rest_v1/page/mobile-sections-lead/' + encodeURIComponent(q),
            request: {},
            headers: {
                Accept: 'application/json; charset=utf-8; profile="https://www.mediawiki.org/wiki/Specs/mobile-sections/0.14.1"',
            },
            name: 'search',
            title: function(response) {
                return response.displaytitle;
            },
            text: function(response) {
                // parse text
                let newText = makeLinksIntoButtons(response.sections[0].text);

                return newText;
            },
            metadata: function(response) {
                return '';
            },
            isEmpty: function(response) {
                return response.sections.length === 0;
            },
        };
        _.assign(search, this.common);
        return search;
    },
    random: function() {
        let random = {
            url: 'https://en.wikipedia.org/api/rest_v1/page/random/summary',
            request: {
                origin: '*',
            },
            headers: {},
            name: 'random',
            title: function(response) {
                return response.title;
            },
            text: function(response) {
                let newText = makeLinksIntoButtons(response.extract_html);
                // return response.extract_html;
                return newText;
            },
            // image: function(response) {
            //     return response;
            // },
            metadata: function(response) {
                return colorPrintMetaData(_.pick(
                    _.assign(
                        {},
                        response,
                        {'connections': '_'}),
                    this.filter), 'Wikipedia');
            },
            isEmpty: function(response) {
                return false;
            },
        };
        _.assign(random, this.common);
        return random;
    },
};

apis.natgeo = {
    name: 'National Geographic',
    common: {
        baseUrl: 'https://newsapi.org/v2',
        key: '1adf231505e349c2810500bee60b6f68',
        style: 'natgeo',
        source: 'national-geographic',
        type: 'image',
        index: 0,
        getNext: function() {
            this.index++;
        },
        image: function(response) {
            if (!response.articles[0].urlToImage) {
                this.getNext();
            }
            return {
                url: response.articles[0].urlToImage,
            };
        },
        title: function(response) {
            return response.articles[0].title;
        },
        isEmpty: function(response) {
            return response.articles.length === 0;
        },
        filter: [
            `articles[0].title`,
            `articles[0].publishedAt`,
            `articles[0].author`,
            `articles[0].description`,
            `articles[0].url`,
        ],
    },
    random: function() {
        let random = {
            // request data
            url: this.common.baseUrl + '/top-headlines',
            request: {
                apiKey: this.common.key,
                sources: this.common.source,
            },
            headers: {},
            name: 'random',
            // response parsing
            metadata: (response) => {
                return colorPrintMetaData(_.pick(_.assign({}, response.articles[0]), this.filter), this.name);
            },
        };
        _.assign(random, this.common);
        return random;
    },
    search: function(query) {
        let search = {
            url: this.common.baseUrl + '/everything',
            request: {
                apiKey: this.common.key,
                sources: this.common.source,
                q: query,
            },
            headers: {},
            name: 'search',
            // response parsing
            metadata: (response) => {
                return colorPrintMetaData(_.pick(_.assign({}, response), this.common.filter), this.name, query);
            },
        };
        _.assign(search, this.common);
        return search;
    },
};

apis.harvard = {
    name: 'Harvard Art Museums',
    common: {
        type: 'image',
        url: 'https://api.harvardartmuseums.org/object',
        headers: {},
        index: 0,
        getNext: function() {
            this.index++;
        },
        image: function(response) {
            if (!response.records[0].images[0]) {
                this.getNext();
            }
            return {
                // url: response.records[0].images[0].baseimageurl,
                url: response.records[0].primaryimageurl,
            };
        },
        title: function(response) {
            return response.records[0].title;
        },
        isEmpty: function(response) {
            return response.records.length === 0;
        },
        key: 'e7c6b540-a099-11e8-8de4-a983889ea599',
        style: 'harvard',
        filter: [
            'records.0.title',
            'records.0.dated',
            'records.0.technique',
            'records.0.classification',
            'records.0.url',
            'records.0.description',
            'records.0.people.0.name',
            'records.0.people.1.name',
        ],
    },
    random: function() {
        let random = {
            request: {
                apikey: this.common.key,
                hasimage: 1,
                sort: 'random',
                size: 1,
            },
            name: 'random',
            metadata: (response) => {
                return colorPrintMetaData(_.omitBy(_.pick(response, this.common.filter).records[0], _.isNil), 'Harvard Art Museums');
            },
        };
        _.assign(random, this.common);
        return random;
    },
    search: function(q) {
        let search = {
            request: {
                apikey: this.common.key,
                hasimage: 1,
                sort: 'random',
                size: 1,
                q: q,
            },
            name: 'search',
            metadata: (response) => {
                return colorPrintMetaData(_.omitBy(_.pick(response, this.common.filter).records[0], _.isNil), 'Harvard Art Museums', q);
            },
        };
        _.assign(search, this.common);
        return search;
    },
};

apis.wiktionary = {
    name: 'Wiktionary',
    common: {
        baseUrl: 'https://en.wiktionary.org/api/rest_v1/page/mobile-sections-remaining/',
        type: 'wiki',
        filter: [],
        getSectionTexts: (sections, type) => {
            let sectionFilter = (fsection) => fsection.line === type;
            let sectionMap = (msection) => msection.text;
            return _.map(_.filter(sections, sectionFilter), sectionMap);
        },
        getWords: function(sections, type) {
            let foundWords = this.getSectionTexts(sections, type);
            if (foundWords.length === 0) return null;
            let regex = /title=\"([a-z]*)\"/g;
            let matches = getMatches(regex, _.join(foundWords));
            return _.join(matches, ', ');
        },
        request: {},
        headers: {},
        // metadata: function(response) {
        //     return JSON.stringify(_.join(this.getSectionTexts(response.sections, this.name), '\n'), null, 2);
        // },
        // title: (_) => q,
        // isEmpty: (response) => response.sections.length === 0,
        style: 'wiktionary',
    },
    antonyms: function(q) {
        let antonyms = {
            url: this.common.baseUrl + encodeURIComponent(q),
            name: 'antonyms',
            text: function(response) {
                let string = `antonyms of ${q}: ${this.getWords(response.sections, 'Antonyms')}`;
                let newText = addIconToNode('wiktionary', string);
                return newText;
            },
            getQuery: () => {
                return q;
            },
        };
        _.assign(antonyms, this.common);
        antonyms.title = (_) => q;
        return antonyms;
    },
    synonyms: function(q) {
        let synonyms = {
            url: this.common.baseUrl + encodeURIComponent(q),
            name: 'synonyms',
            text: function(response) {
                let string = `synonyms of ${q}: ${this.getWords(response.sections, 'Synonyms')}`;
                let newText = addIconToNode('wiktionary', string);
                return newText;
            },
            getQuery: () => {
                return q;
            },
        };
        _.assign(synonyms, this.common);
        synonyms.title = (_) => q;
        return synonyms;
    },
    definition: function(q) {
        let def = {
            url: `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(q)}`,
            name: 'definition',
            text: (response) => {
                if (response.en[0]) {
                    let strippedDef = stripHTML(response.en[0].definitions[0].definition);
                    let string = `${q}: ${strippedDef}`;
                    let newText = addIconToNode('wiktionary', string);
                    return newText;
                } else {
                    return `definition for ${q} not found`;
                }
            },
            getQuery: () => {
                return q;
            },
        };
        _.assign(def, this.common);
        def.title = (_) => q;
        def.metadata = (response) => colorPrintMetaData(response.en[0], 'Wiktionary', q);
        def.isEmpty = (response) => !response.en || response.en.length === 0;
        return def;
    },
    random: function() {
        let random = {
            url: 'http://api.conceptnet.io/c/en',
            type: 'wiki',
            request: {
                // source: '/s/resource/wiktionary/en',
                rel: '/r/RelatedTo',
                limit: 1,
                offset: Math.floor(Math.random() * 4999),
            },
            name: 'random',
            headers: {},
            metadata: (response) => response,
            title: (response) => `${response.edges[0].start.label}`,
            text: (response) => {
                let stringText = `${response.edges[0].start.label} --${response.edges[0].rel.label}--> ${response.edges[0].end.label}`;
                let newText = addIconToNode('wiktionary', stringText);
                return newText;
            },
            isEmpty: (_) => false,
        };
        _.assign(random, this.common);
        return random;
    },
    search: function(q) {
        return this.definition(q);
    },
};

apis.scientist = {
    name: 'New Scientist',
    common: {
        baseUrl: 'https://newsapi.org/v2',
        key: '1adf231505e349c2810500bee60b6f68',
        style: 'scientist',
        source: 'new-scientist',
    },
    random: function() {
        let random = {
            // request data
            type: 'wiki',
            url: this.common.baseUrl + '/top-headlines',
            request: {
                apiKey: this.common.key,
                sources: this.common.source,
            },
            headers: {},
            name: 'random',
            // response parsing
            text: function(response) {
                let string = response.articles[0].description +
                             '\n' + response.articles[0].url;
                let newText = addIconToNode('news', string);
                return newText;
            },
            isEmpty: function(response) {
                return false;
            },
            title: function(response) {
                return response.articles[0].title;
            },
        };
        _.assign(random, this.common);
        return random;
    },
    search: function(query) {
        let search = {
            type: 'wiki',
            url: this.common.baseUrl + '/everything',
            request: {
                apiKey: this.common.key,
                sources: this.common.source,
                q: query,
            },
            headers: {},
            name: 'search',
            // response parsing
            text: function(response) {
                if (response.articles.length > 0) {
                    let string = response.articles[0].description + '\n' + response.articles[0].url;
                    let newText = addIconToNode('news', string);
                    return newText;
                } else return;
            },
            image: function(response) {
                return {
                    url: response.articles[0].urlToImage,
                };
            },
            isEmpty: function(response) {
                return response.articles.length === 0;
            },
            title: function(response) {
                return response.articles[0].title;
            },
        };
        _.assign(search, this.common);
        return search;
    },
};

apis.met = {
    name: 'The Met',
    common: {
        baseUrl: 'https://collectionapi.metmuseum.org/public/collection/v1/objects/',
        style: 'met',
        type: 'image',
        index: 0,
        getNext: function() {
            this.index++;
        },
        image: function(response) {
            // if (!response.primaryImage){

            // }
            return {
                url: response.primaryImage,
            };
        },
        title: function(response) {
            return response.title;
        },
        isEmpty: function(response) {
            return response.length === 0;
        },
        filter: [
            `articles[0].title`,
            `articles[0].publishedAt`,
            `articles[0].author`,
            `articles[0].description`,
            `articles[0].url`,
        ],
    },
    random: function() {
        let random = {
            // request data
            url: this.common.baseUrl + Math.round(Math.random()*471581).toString(),
            headers: {},
            name: 'random',
            // response parsing
            metadata: (response) => {
                return colorPrintMetaData(_.pick(_.assign({}, response.articles[0]), this.filter), this.name);
            },
        };
        _.assign(random, this.common);
        return random;
    },
    // search: function(query) {
    //     let search = {
    //         url: this.common.baseUrl + '/everything',
    //         request: {
    //             apiKey: this.common.key,
    //             sources: this.common.source,
    //             q: query,
    //         },
    //         headers: {},
    //         name: 'search',
    //         // response parsing
    //         metadata: (response) => {
    //             return colorPrintMetaData(_.pick(_.assign({}, response), this.common.filter), this.name, query);
    //         },
    //     };
    //     _.assign(search, this.common);
    //     return search;
    // },
};

// not working
apis.wordnet = {
    name: 'Wordnet',
    common: {
        baseUrl: 'http://us-central1-pathways-8a150.cloudfunctions.net/',
        type: 'wiki',
        style: 'wordnet',
    },
    search: function(q) {
        return this.synonyms(q);
    },
    synonyms: function(word) {
        let synonyms = {
            request: {
                query: word,
            },
            url: this.common.baseUrl + 'getSynonyms',
            text: (response) => _.join(response.synonyms, ', '),
            metadata: (response) => colorPrintMetaData(response),
            title: (response) => response.query,
            isEmpty: (response) => response.total === 0,
        };
        return synonyms;
    },
    // antonyms: function(word) {
    //     let antonyms = {
    //         request: {
    //             query: word,
    //         },
    //         url: this.common.url + 'getAntonyms',
    //         text: (response) => _.join(response.antonyms, ', '),
    //         metadata: (response) => colorPrintMetaData(response),
    //         title: (response) => response.query,
    //         isEmpty: (response) => response.total === 0,
    //     };
    //     return antonyms;
    // },
};
// not working
apis.quotes = {
    common: {
        url: 'http://quotesondesign.com/wp-json/posts',
        type: 'wiki',
    },
    random: function() {
        let random = {
            request: {
                'filter[orderby]': 'rand',
                'filter[posts_per_page]': 1,
                'callback': '',
            },
            text: (response) => response[0].content,
            metadata: (response) => colorPrintMetaData(response[0]),
            title: (response) => response[0].title,
            isEmpty: (_) => false,
            style: 'quotes',
        };
        _.assign(random, this.common);
        return random;
    },
};
// not working
apis.quotes2 = {
    common: {
        url: 'http://api.forismatic.com/api/1.0/?method=getQuote&lang=en&format=jsonp&jsonp=?',
        type: 'wiki',
    },
    random: function() {
        let random = {
            request: {
                // // 'origin': '*',
                // 'method': 'getQuote',
                // 'format': 'json',
                // // 'key': 123456,
                // 'lang': 'en',
                // 'format': 'jsonp',
            },
            headers: {},
            text: (response) => response.quoteText,
            metadata: (response) => colorPrintMetaData(response),
            title: (response) => response.quoteAuthor,
            isEmpty: (_) => false,
        };
        _.assign(random, this.common);
        return random;
    },
};
apis.googleimages = {
    name: 'Google Image Search',
    common: {
        type: 'image',
        url: 'https://www.googleapis.com/customsearch/v1?cx=016909358668369373546:byitefvq77w',
        cx: '016909358668369373546:byitefvq77w',
        key: 'AIzaSyCgW9bxN6xZIhX-aGbak0_yVTBDbPKwbmE',
    },
    search: function(q) {
        let search = {
            request: {
                q: q,
                cx: this.common.cse,
                key: this.common.key,
            },
            headers: {},
            image: (response) => {
                return {
                    url: response.items[0].pagemap.cse_image[0].src,
                };
            },
            metadata: (response) => colorPrintMetaData(response.items[0]),
            title: (response) => response.items[0],
            isEmpty: (response) => response.items.length === 0,
            style: 'googleimages',
        };
        _.assign(search, this.common);
        return search;
    },
};

// not working
apis.artsy = {
    // name: 'Artsy Art Database',
    // common: {
    //     type: 'image',
    //     /* EXPIRES 08/21 (every week) */
    //     token: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJyb2xlcyI6IiIsImV4cCI6MTUzNDg4Njg2MSwiaWF0IjoxNTM0MjgyMDYxLCJhdWQiOiI1YjRiOTgxNTFiMzdkNTY1MWJiMTZlM2EiLCJpc3MiOiJHcmF2aXR5IiwianRpIjoiNWI3MzQ5NGRhZTIxNTIyMWY1ZDk4Y2RjIn0.GsLa1NIPVh4Nn7H8i5LpYFEO0zj0WCKYklfAmyfFDro',
    //     filter: [
    //         'title',
    //         'category',
    //         'medium',
    //         'date',
    //         '_links.permalink.href',
    //         '_links.thumbnail.href',
    //         'connections',
    //     ],
    //     style: 'artsy',
    // },
    // random: function() {
    //     let random = {
    //         url: 'https://api.artsy.net/api/artworks',
    //         name: 'random',
    //         request: {
    //             sample: '',
    //         },
    //         headers: {
    //             'X-Xapp-Token': this.common.token,
    //             'Accept': 'application/vnd.artsy-v2+json',
    //         },
    //         image: function(response) {
    //             let url = response._links.thumbnail.href;
    //             return {
    //                 width: 230,
    //                 height: 230,
    //                 url: url,
    //             };
    //         },
    //         title: function(response) {
    //             return response.title;
    //         },
    //         metadata: function(response) {
    //             const filteredMetaData = _.pick(
    //                 _.assign(
    //                     {},
    //                     response,
    //                     {'connections': '_'}),
    //                 this.filter);
    //             return colorPrintMetaData(filteredMetaData, 'Artsy');
    //         },
    //         isEmpty: function(response) {
    //             return false;
    //         },
    //     };
    //     _.assign(random, this.common);
    //     return random;
    // },
    // search: function(q) {
    //     let search = {
    //         url: 'https://api.artsy.net/api/search',
    //         name: 'search',
    //         request: {
    //             q: q,
    //             type: 'artwork',
    //         },
    //         headers: {
    //             'X-Xapp-Token': this.common.token,
    //         },
    //         index: 0,
    //         getNext: function() {
    //             this.index++;
    //         },
    //         image: function(response) {
    //             if (!response._embedded.results[this.index]._links.thumbnail) {
    //                 this.getNext();
    //             }
    //             let url = response._embedded.results[this.index]._links.thumbnail.href;
    //             return {
    //                 width: 230,
    //                 height: 230,
    //                 url: url,
    //             };
    //         },
    //         metadata: function(response) {
    //             const filteredMetaData = _.pick(
    //                 _.assign(
    //                     {},
    //                     response._embedded.results[this.index],
    //                     {'connections': '_'}),
    //                 this.filter);
    //             return colorPrintMetaData(filteredMetaData, 'Artsy', q);
    //         },
    //         title: function(response) {
    //             return response._embedded.results[this.index].title;
    //         },
    //         isEmpty: function(response) {
    //             return !(response._embedded.results && response._embedded.results.length > 0);
    //         },
    //     };
    //     _.assign(search, this.common);
    //     return search;
    // },
};

/*
class ContentSource {
    constructor(name, type) {
        this.name = name;
        this.type = type;
    }

    setCommon(common) {
        this.common = common;
    }

    addFunction(name, fun) {
        this[name] = (q) => {
            return _.assign(fun(q), this.common);
        }
    }
}

class ContentSourceFunction {
    constructor(common, fun) {
        this.common = common;
        this.fun = fun;
    }

    setFun(fun) {
        this.fun = fun;
    }

    setCommon(common) {
        this.common = common;
    }

    addCommon(a, b) {

    }

    setFilter(filter) {
        this.filter = filter;
    }
}
function makeNewAPI(abbr, name, type, baseURL) {
    apis[abbr] = {
        name: name,
        common: {
            type: type,
            baseURL: baseURL
        }
    }

}

//declation automatically exports
new ContentSource({
    abbr: 'abbr',
    name: 'Full Name',
    type: 'wiki'
})

new ContentSource('abbr', {
    name: 'Full Name',
    type: 'wiki'
})


// or
// making new content source,
let g = new ContentSource('Full Name', 'type')
g.setCommon({
    baseURL: 'google.com',
    etc: 'etc'
})

g.addFunction('random', function() {

})

g.addFunction('search', function(q) {

})
// g.addRandomFunction
// g.addCustomFunction('')

// g.delegateFunction('search', 'antonyms');


// apis.newAPI = g;

class APIResponse {
    constructor(image) {
        this.image = image;
    }

    image() {
        return {
            width: this.response.width,
            height: this.response.height,
            url: this.response.url
        }
    }
    title() {
        return this.response.title;
    }
    metadata() {
        return colorPrintMetaData(this.response, 'Test API Name');
    }
    isEmpty() {
        return this.response.total === 0;
    }
} */

// apis.mock = {
//     // add a name
//     name: 'Offline Test API',
//     random: function() {
//         return {
//             type: 'image',
//             url: '/mockapiresponse.json',
//             request: {},
//             headers: {},
//             image: function(response) {
//                 return {
//                     width: response.width,
//                     height: response.height,
//                     url: response.url,
//                 };
//             },
//             title: function(response) {
//                 return response.title;
//             },
//             metadata: function(response) {
//                 return colorPrintMetaData(response, 'Mock API');
//             },
//             isEmpty: function(response) {
//                 return response.total === 0;
//             },
//         };
//     },
//     search: function(q) {
//         return this.random();
//     },
//     exampleResponse: {
//         width: 160,
//         height: 160,
//         url: '/imgs/mock.png',
//         title: 'test title',
//         total: 1,
//     },
// };

/**
 * ADD APIS ABOVE
 */
export default apis;
